// Base API Client with common functionality

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import i18n from '../i18n';
import { getAccessToken } from '../lib/authUtils';
import { getCurrentWorkspaceSlug } from '../lib/workspaceUtils';
import { tokenRefreshService } from '../services/TokenRefreshService';
import { notificationStore } from '../stores/NotificationStore';
import { DEFAULT_DEV_TOKEN, DEV_TOKENS } from './devTokens';
import { validateApiError } from './schemas';

// Extend AxiosRequestConfig to include _retry flag
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Custom error for unknown workspace
export class UnknownWorkspaceError extends Error {
  public readonly workspaceSlug: string;
  public readonly code: string;

  constructor(workspaceSlug: string) {
    super(`Unknown workspace: ${workspaceSlug}`);
    this.name = 'UnknownWorkspaceError';
    this.workspaceSlug = workspaceSlug;
    this.code = 'unknown_workspace';
  }
}

export class ApiError extends Error {
  public readonly errorMessage: string;
  public readonly data: Record<string, unknown>;
  public readonly timestamp: number;

  constructor(errorData: {
    errorMessage: string;
    data: Record<string, unknown>;
    timestamp: number;
  }) {
    super(errorData.errorMessage);
    this.name = 'ApiError';
    this.errorMessage = errorData.errorMessage;
    this.data = errorData.data;
    this.timestamp = errorData.timestamp;
  }
}

export class ApiConflictError extends Error {
  constructor() {
    super('Conflict');
  }
}

export interface BaseApiClientConfig {
  prefix: string;
}

export abstract class BaseApiClient {
  protected readonly axiosInstance: AxiosInstance;

  constructor(config: BaseApiClientConfig) {
    const baseURL = this.getBaseUrl(config.prefix);

    // Get token from localStorage or use default participant token
    const getDebugToken = () => {
      const debugToken = localStorage.getItem('debug_token');

      if (
        !debugToken ||
        DEV_TOKENS.every((token) => token.token !== debugToken)
      ) {
        const defaultToken = DEFAULT_DEV_TOKEN;

        localStorage.setItem('debug_token', defaultToken.token);
        localStorage.setItem('debug_token_type', defaultToken.key);

        return defaultToken.token;
      }

      return debugToken;
    };

    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        // dev, TODO remove
        Authorization:
          process.env.REACT_APP_DEBUG_AUTH_ENABLED === 'true'
            ? `Bearer ${getDebugToken()}`
            : undefined,
      },
    });

    if (process.env.REACT_APP_DEBUG_AUTH_ENABLED !== 'true') {
      // Add authorization interceptor
      this.setupAuthInterceptor();

      // Add response interceptor for token refresh
      this.setupRefreshTokensInterceptor();
    }

    // Add workspace slug to all requests
    this.setupWorkspaceInterceptor();
  }

  /**
   * Get base URL from environment or use default with client prefix
   */
  private getBaseUrl(clientPrefix: string): string {
    // For development, use REACT_APP_API_BASE_URL if provided
    // For production, use current origin + /api
    const envBaseUrl = process.env.REACT_APP_API_BASE_URL;

    if (envBaseUrl) {
      return `${envBaseUrl}/api/${clientPrefix}`;
    }

    // Default to current origin + /api + client prefix for production
    return `/api/${clientPrefix}`;
  }

  /**
   * Generic method to make HTTP requests
   */
  protected async makeRequest<T>(
    endpoint: string,
    options: {
      method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
      data?: Record<string, any>;
      params?: Record<string, any>;
    } = {}
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.request({
        url: endpoint,
        method: options.method || 'GET',
        data: options.data,
        params: options.params,
      });

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorData = error.response.data;
        const statusCode = error.response.status;

        // Show appropriate toast notification based on status code
        switch (statusCode) {
          case 401:
            notificationStore.showError(i18n.t('errors.unauthorized'));
            break;
          case 403:
            notificationStore.showError(i18n.t('errors.forbidden'));
            break;
          case 404:
            notificationStore.showError(i18n.t('errors.notFound'));
            break;
          case 409:
            // Don't show error toast for 409 - let the calling code handle it
            break;
          case 422:
            notificationStore.showError(i18n.t('errors.validationError'));
            break;
          case 429:
            notificationStore.showError(i18n.t('errors.tooManyRequests'));
            break;
          case 500:
            notificationStore.showError(i18n.t('errors.serverError'));
            break;
          case 502:
          case 503:
          case 504:
            notificationStore.showError(i18n.t('errors.serviceUnavailable'));
            break;
          default:
            notificationStore.showError(i18n.t('errors.requestError'));
        }

        // Validate API error structure
        const validation = validateApiError(errorData);

        if (validation.isValid && validation.apiError) {
          // Check for unknown workspace error
          if (validation.isUnknownWorkspace) {
            const workspaceSlug = getCurrentWorkspaceSlug();

            // Quickfix
            // Ideas is to redirect to root with reloading the page to reinitialize
            // the app (and autoselect the workspace)
            if (window.location.pathname !== '/') {
              window.history.pushState({}, '', '/');
              window.location.reload();
            }

            throw new UnknownWorkspaceError(workspaceSlug);
          }

          // For 409 errors, throw a special error that can be handled by calling code
          if (statusCode === 409) {
            throw new ApiConflictError();
          }

          // For standard API errors, show toast and throw a generic error
          // that won't crash the React app
          throw new Error('API request failed');
        }

        // For unhandled HTTP errors, show toast and throw generic error
        throw new Error('HTTP request failed');
      } else if (
        error instanceof AxiosError &&
        error.code === 'NETWORK_ERROR'
      ) {
        // Network error (no internet connection)
        notificationStore.showError(i18n.t('errors.networkError'));
        throw new Error('Network error');
      } else if (error instanceof AxiosError && error.code === 'ECONNABORTED') {
        // Request timeout
        notificationStore.showError(i18n.t('errors.timeoutError'));
        throw new Error('Request timeout');
      } else {
        // Generic error
        notificationStore.showError(i18n.t('errors.appError'));
        throw new Error('Unexpected error');
      }
    }
  }

  /**
   * Setup authorization interceptor to add Bearer token
   */
  private setupAuthInterceptor(): void {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Get token from memory
        const token = getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Setup response interceptor to handle token refresh on 401 errors
   */
  private setupRefreshTokensInterceptor(): void {
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        // Check if it's a 401 error and we haven't already tried to refresh
        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            // Try to refresh the token using the service
            const success = await tokenRefreshService.refreshTokens();

            if (success) {
              // Get the new access token
              const newToken = getAccessToken();
              if (newToken) {
                // Update the original request with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`;

                // Retry the original request
                return this.axiosInstance.request(originalRequest);
              }
            }

            // Refresh failed, throw original error
            throw error;
          } catch {
            // Refresh failed, throw original error
            throw error;
          }
        }

        // For other errors or if refresh failed, throw the original error
        throw error;
      }
    );
  }

  /**
   * Setup workspace interceptor to add x-workspace header
   */
  private setupWorkspaceInterceptor(): void {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        try {
          const workspaceSlug = getCurrentWorkspaceSlug();
          config.headers['x-workspace'] = workspaceSlug;
        } catch (error) {
          // If workspace slug is not available, don't add header
          // This allows the backend to handle the case appropriately
          console.warn('Workspace slug not found in URL:', error);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Configure axios interceptors if needed
   */
  protected configureInterceptors(): void {
    // Can be overridden by child classes for specific interceptors
  }
}
